/**
 * keycloakAuthService — webapp variant.
 *
 * Shared across PE / admin / webapp by filename (see SHARED_FILES.md).
 * Contents legitimately differ today; the divergences are:
 *
 *   - Reads APP_SOURCE from validated ENV (`@/config/env`) rather than from
 *     `utils/endpoints` like admin does, because webapp's env layer fails
 *     loudly on missing vars.
 *   - `loginUser` / `ssoCallback` return a flatter `{ success, message, _data }`
 *     envelope (with `_data._user`) tuned to webapp's auth slice; admin returns
 *     the richer `{ success, message, data: { user, credentials, roles, ... } }`
 *     shape.
 *   - `registerUser` accepts webapp-only fields (`is_organization`,
 *     `organization_name`) that admin's register endpoint doesn't take.
 *   - `ssoCallback` is webapp-only (admin's equivalent lives in
 *     keycloakDirectAuth.ts as `completeKeycloakCallback`).
 *   - `storeAuthData` delegates to the sync-cached `setAuthData` from
 *     `secureAuthStorage` so that synchronous readers (useSessionTimer,
 *     getRefreshTokenExpiresIn, isAccessTokenValid…) see the new credentials
 *     immediately. Admin's version writes directly to SecureStorage.
 *   - Includes `getProfileAPI`, `updateProfileAPI`, `uploadAvatarAPI`,
 *     `changePasswordAPI`, `syncUserToSecureStorage` whose endpoints + headers
 *     diverge from admin's (no inline Authorization header — webapp relies on
 *     the shared `apiClient` interceptor instead).
 */
import axios from 'axios';
import ENDPOINTS from '../utils/endpoints';
import { ENV } from '../config/env';
import { SecureStorage } from '../utils/security';
import {
  setAuthData,
  clearAuthStorage as clearAuthStorageCache,
  type Credentials as StoredCredentials,
  type User as StoredUser,
} from '../utils/secureAuthStorage';

// ============================================
// Type Definitions
// ============================================

interface RegisterRequestBody {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  is_organization: boolean;
  organization_name?: string;
  app_source: string;
  captcha_token?: string;
}

interface AuthUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  keycloak_roles?: string[];
  [key: string]: unknown;
}

interface AuthCredentials {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  access_token_expires_at?: number;
  refresh_token_expires_at?: number;
}

type UserRole = string;
type Permission = string;

// ============================================
// Authentication API Calls
// ============================================

export const loginUser = async (credentials: {
  email: string;
  password: string;
  captcha_token?: string;
}) => {
  // Get app source from environment variable
  const appSource = ENV.APP_SOURCE;

  // Build request body with honeypot fields for bot detection
  const requestBody: Record<string, string | undefined> = {
    email: credentials.email,
    password: credentials.password,
    app_source: appSource,
    auth_mode: 'token',
    website: '', // Honeypot field - should remain empty
    fax_number: '', // Honeypot field - should remain empty
  };

  // Add captcha token if provided
  if (credentials.captcha_token) {
    requestBody.captcha_token = credentials.captcha_token;
  }

  const response = await axios.post(ENDPOINTS.LOGIN, requestBody);

  // Transform the response to match expected format
  // API returns: { status: "success", _data: { tokens, user, permissions } }
  // We need: { success: true, _data: { user, credentials, roles, permissions } }
  if (response.data.status === 'success') {
    // Defensive coercion — the backend response is typed loosely and
    // may omit first_name / last_name / full_name. Without this,
    // the store would hold undefined name fields and any downstream
    // consumer (e.g. the WP vote endpoint's user_id/name payload)
    // would silently drop them, producing "Missing parameter(s)"
    // 400s. Mirror the shape of useHydrateAuth's hydration path.
    const u = response.data.data.user;
    const firstName: string = u.first_name ?? '';
    const lastName: string = u.last_name ?? '';
    const fullName: string =
      (u.full_name && String(u.full_name).trim()) ||
      (u.name && String(u.name).trim()) ||
      `${firstName} ${lastName}`.trim() ||
      u.email;

    return {
      success: true,
      message: response.data.message,
      _data: {
        _user: {
          ...u,
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
        },
        credentials: response.data.data.tokens, // Map 'tokens' to 'credentials'
        roles: u.keycloak_roles || [], // Extract roles from user
        permissions: response.data.data.permissions || [],
      },
    };
  }

  // Handle unsuccessful response
  return {
    success: false,
    message: response.data.message || 'Login failed',
    _data: null,
  };
};

export const registerUser = async (data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isOrganization: boolean;
  organizationName?: string;
  captcha_token?: string;
}) => {
  // Get app source from environment variable
  const appSource = ENV.APP_SOURCE;

  // Build the request body
  const requestBody: RegisterRequestBody = {
    email: data.email,
    password: data.password,
    first_name: data.firstName,
    last_name: data.lastName,
    phone: data.phoneNumber,
    is_organization: data.isOrganization,
    app_source: appSource,
  };

  // Only add organization_name if it's an organization account
  if (data.isOrganization && data.organizationName) {
    requestBody.organization_name = data.organizationName;
  }

  // Add captcha token if provided
  if (data.captcha_token) {
    requestBody.captcha_token = data.captcha_token;
  }

  const response = await axios.post(ENDPOINTS.REGISTER, requestBody);
  return response.data;
};

export const forgotPasswordRequest = async (email: string, captcha_token?: string) => {
  const requestBody: { email: string; captcha_token?: string } = { email };

  // Add captcha token if provided
  if (captcha_token) {
    requestBody.captcha_token = captcha_token;
  }

  const response = await axios.post(ENDPOINTS.FORGOT_PASSWORD, requestBody);
  return response.data;
};

// Exchange a Keycloak authorization code (returned to /auth/callback after
// Google sign-in) for our app session. The backend POSTs to Keycloak's /token
// endpoint with our PKCE code_verifier, then returns:
//   { data: { tokens, user, permissions, has_active_subscription }, message }
// (No `status` field — success is signaled by HTTP 200 + presence of tokens.)
export const ssoCallback = async (
  code: string,
  redirect_uri: string,
  code_verifier?: string | null
) => {
  const response = await axios.post(ENDPOINTS.SSO_CALLBACK, {
    code,
    redirect_uri,
    app_source: ENV.APP_SOURCE,
    ...(code_verifier ? { code_verifier } : {}),
  });

  const body = (response.data ?? {}) as {
    data?: {
      tokens?: Record<string, unknown>;
      user?: Record<string, unknown>;
      permissions?: unknown[];
    };
    message?: string;
  };
  const data = body.data ?? {};
  const tokens = data.tokens;
  const user = data.user;

  if (tokens && user) {
    // Keycloak SSO sometimes omits refresh_expires_in; default to 30 days so
    // storeAuthData() doesn't end up writing NaN into the refresh expiry.
    const refreshExpiresIn = (tokens.refresh_expires_in as number | undefined) ?? 30 * 24 * 3600;

    // Roles: prefer keycloak_roles[], fall back to a single `role` string.
    const rolesFromUser =
      (user.keycloak_roles as string[] | undefined) ?? (user.role ? [user.role as string] : []);

    return {
      success: true,
      message: body.message,
      _data: {
        _user: user,
        credentials: {
          ...tokens,
          refresh_expires_in: refreshExpiresIn,
        } as unknown as AuthCredentials,
        roles: rolesFromUser,
        permissions: (data.permissions ?? []) as Permission[],
      },
    };
  }

  return {
    success: false,
    message: body.message ?? 'SSO sign-in failed',
    _data: null,
  };
};

export const refreshToken = async (refreshToken: string) => {
  // The backend's /v1/auth/refresh route is gated by the auth.keycloak
  // middleware, so it expects a Bearer access_token attached even though
  // the body carries the refresh_token. The shared axios interceptor
  // intentionally skips bearer-attachment for this URL (to avoid refresh
  // loops), so we attach the header explicitly here. Without this, the
  // request returns 401 and the response interceptor immediately bounces
  // the user to /login — never reaching the "Stay signed in" success path.
  // Mirrors admin (keycloakAuthService.refreshToken) + postexchange
  // (authService.refreshTokenAPI).
  const credentials = await getStoredCredentials();
  const accessToken = credentials?.access_token;
  const response = await axios.post(
    ENDPOINTS.REFRESH_TOKEN,
    { refresh_token: refreshToken },
    {
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    }
  );
  return response.data;
};

export const logoutUser = async (accessToken: string) => {
  const response = await axios.post(
    ENDPOINTS.LOGOUT,
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

// ============================================
// Token Validation Helpers (Async for SecureStorage)
// ============================================

export const isAccessTokenValid = async (): Promise<boolean> => {
  const credentials = await getStoredCredentials();
  if (!credentials) return false;
  const now = Math.floor(Date.now() / 1000);
  return now < (credentials.access_token_expires_at ?? 0);
};

export const isRefreshTokenValid = async (): Promise<boolean> => {
  const credentials = await getStoredCredentials();
  if (!credentials) return false;
  const now = Math.floor(Date.now() / 1000);
  return now < (credentials.refresh_token_expires_at ?? 0);
};

// ============================================
// SecureStorage Helpers (Browser Only) - AES-256-GCM Encrypted
// ============================================

export const getStoredCredentials = async (): Promise<AuthCredentials | null> => {
  if (typeof window === 'undefined') return null;
  const stored = await SecureStorage.getItem('auth_credentials');
  return stored ? JSON.parse(stored) : null;
};

export const getStoredUser = async (): Promise<AuthUser | null> => {
  if (typeof window === 'undefined') return null;
  const stored = await SecureStorage.getItem('auth_user');
  return stored ? JSON.parse(stored) : null;
};

export const getStoredRoles = async (): Promise<UserRole[] | null> => {
  if (typeof window === 'undefined') return null;
  const stored = await SecureStorage.getItem('auth_roles');
  return stored ? JSON.parse(stored) : null;
};

export const getStoredPermissions = async (): Promise<Permission[]> => {
  if (typeof window === 'undefined') return [];
  const stored = await SecureStorage.getItem('auth_permissions');
  return stored ? JSON.parse(stored) : [];
};

export const clearAuthStorage = (): void => {
  if (typeof window === 'undefined') return;
  // Clear in-memory cache + encrypted disk + legacy keys via the canonical
  // implementation in secureAuthStorage. Doing this here-only would leave
  // the cache populated and let sync getters return stale credentials.
  clearAuthStorageCache();
  SecureStorage.removeItem('auth_remember');
  sessionStorage.removeItem('session_alive');
};

export const getRememberMe = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return true;
  const stored = await SecureStorage.getItem('auth_remember');
  return stored ? JSON.parse(stored) : true;
};

export const storeAuthData = async (
  user: AuthUser,
  credentials: AuthCredentials,
  roles: UserRole[],
  permissions: Permission[],
  rememberMe: boolean = false
): Promise<void> => {
  if (typeof window === 'undefined') return;

  // Calculate expiry timestamps
  const now = Math.floor(Date.now() / 1000);
  let accessExpiresAt = now + credentials.expires_in;
  let refreshExpiresAt = now + credentials.refresh_expires_in;

  // If "Remember me" is unchecked, cap session to 24 hours
  if (!rememberMe) {
    const maxExpiry = now + 86400; // 24 hours
    accessExpiresAt = Math.min(accessExpiresAt, maxExpiry);
    refreshExpiresAt = Math.min(refreshExpiresAt, maxExpiry);
  }

  const credentialsWithExpiry = {
    ...credentials,
    access_token_expires_at: accessExpiresAt,
    refresh_token_expires_at: refreshExpiresAt,
  };

  // Persist encrypted to disk AND populate the in-memory cache so sync
  // readers (useSessionTimer, getRefreshTokenExpiresIn, isAccessTokenValid…)
  // see the new credentials immediately, not after the next page reload.
  await setAuthData({
    user: user as unknown as StoredUser,
    credentials: credentialsWithExpiry as unknown as StoredCredentials,
    roles: roles as unknown as string[],
    permissions: permissions as unknown as string[],
  });

  // Remember-me + session-alive markers aren't part of setAuthData's contract.
  await SecureStorage.setItem('auth_remember', JSON.stringify(rememberMe));
  if (!rememberMe) {
    sessionStorage.setItem('session_alive', '1');
  } else {
    sessionStorage.removeItem('session_alive');
  }
};

// ============================================
// Profile API Calls
// ============================================

/**
 * GET /api/v1/auth/me - Fetch current user profile
 */
export const getProfileAPI = async () => {
  const response = await axios.get(ENDPOINTS.GET_PROFILE);
  return response.data;
};

/**
 * PATCH /api/v1/auth/me - Update profile fields
 */
export const updateProfileAPI = async (data: {
  first_name?: string;
  last_name?: string;
  phone?: string;
}) => {
  const response = await axios.patch(ENDPOINTS.UPDATE_PROFILE, data);
  return response.data;
};

/**
 * POST /api/v1/auth/me/avatar - Upload avatar image
 */
export const uploadAvatarAPI = async (file: File) => {
  const formData = new FormData();
  formData.append('avatar', file);

  const response = await axios.post(ENDPOINTS.UPLOAD_AVATAR, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * POST /api/v1/auth/change-password - Change user password
 */
export const changePasswordAPI = async (data: {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}) => {
  const response = await axios.post(ENDPOINTS.CHANGE_PASSWORD, data);
  return response.data;
};

/**
 * Sync partial user updates to SecureStorage
 */
export const syncUserToSecureStorage = async (updates: Partial<AuthUser>): Promise<void> => {
  const storedUser = await getStoredUser();
  if (storedUser) {
    const updatedUser = { ...storedUser, ...updates };
    await SecureStorage.setItem('auth_user', JSON.stringify(updatedUser));
  }
};
